import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { ArrowUp, GitPullRequest, PanelLeft, PanelRight, Plus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { getDraft, saveDraftDebounced } from "../agent-session/chatDrafts";
import { MIN_RIGHT_SIDEBAR_WIDTH } from "../agent-session/hooks/useRightSidebar";
import { readCachedSessionHeaderChrome } from "../agent-session/sessionHeaderChrome";
import { peekSkeletonComposerText, readCachedShowAutofixCard, saveSkeletonComposerText } from "./newSessionSkeletonState";
import { NEW_SESSION_MASCOT_SIZE_CLASS, NEW_SESSION_SCROLL_AREA_CLASS, NEW_SESSION_WELCOME_COLUMN_CLASS } from "./newSessionLayout";
import { PROMPT_PILL_ROW_CLASS, PromptPillSkeleton, RIGHTSIZE_STARTER_PILL_LAYOUT_CLASS, STARTER_PILL_ROW_CLASS, StarterPromptPillSkeleton, SuggestionCardSkeleton } from "./promptPills";
import { ShortcutsPill } from "./ShortcutsPill";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { readLeftSidebarSkeletonLayout, readRightSidebarSkeletonWidth, useIsHydrated } from "./skeletonLayout";
import { assetUrl } from "@/lib/assetUrl";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionChatSkeleton     – chat content only (header, messages, input).
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

interface SessionChatSkeletonProps {
  /**
   * Right details panel state resolved by the caller against its measured
   * container (Codesmith.tsx has the exact space budget). Omitted, the
   * skeleton best-efforts it from the persisted preference and the viewport.
   */
  rightSidebar?: {
    open: boolean;
    width: number;
  };
  sessionId?: string;
  /**
   * Title / PR pill already known from the session list. Wins over the
   * last-rendered cache so a sidebar click doesn't wait for a prior visit.
   */
  header?: {
    title?: string | null;
    prLabel?: string | null;
  };
}

/**
 * Placeholder for the right details panel, mirroring AgentSessionView's
 * fixed-width flex sibling so the chat column doesn't shift when the real
 * panel mounts. Widget cards match SidebarWidget's container styling.
 */
function RightSidebarSkeleton(t0) {
  const $ = _c(8);
  const {
    width
  } = t0;
  let t1;
  if ($[0] !== width) {
    t1 = {
      width
    };
    $[0] = width;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-10 items-center px-4"><Skeleton className="h-3.5 w-24 rounded" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="rounded-lg border border-[var(--color-border)] bg-white dark:bg-[var(--color-app-background)]">{t2}<div className="space-y-2 px-3 pb-3"><Skeleton className="h-3.5 w-full rounded" /><Skeleton className="h-3.5 w-4/5 rounded" /><Skeleton className="h-3.5 w-3/5 rounded" /></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="rounded-lg border border-[var(--color-border)] bg-white dark:bg-[var(--color-app-background)]"><div className="flex h-10 items-center justify-between px-4"><Skeleton className="h-3.5 w-20 rounded" /><Skeleton className="h-3.5 w-6 rounded" /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex h-full flex-col gap-3 overflow-hidden px-3 py-3">{t3}{t4}<div className="rounded-lg border border-[var(--color-border)] bg-white dark:bg-[var(--color-app-background)]"><div className="flex h-10 items-center px-4"><Skeleton className="h-3.5 w-16 rounded" /></div></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1) {
    t6 = <div style={t1} className="relative flex-shrink-0 bg-[var(--color-app-background)]" data-slot="right-sidebar-skeleton">{t5}</div>;
    $[6] = t1;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}

/** Chat content skeleton — no left sidebar, just header + messages + input. */
export function SessionChatSkeleton(t0) {
  const $ = _c(63);
  const {
    rightSidebar,
    sessionId,
    header
  } = t0;
  const isHydrated = useIsHydrated();
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== sessionId) {
    t1 = () => {
      const input = inputRef.current;
      if (!sessionId || !input || document.activeElement !== input) {
        return;
      }
      input.setSelectionRange(input.value.length, input.value.length);
    };
    t2 = [sessionId];
    $[0] = sessionId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let rightPanelWidth = null;
  if (isHydrated) {
    let t3;
    if ($[3] !== rightSidebar) {
      t3 = rightSidebar ? rightSidebar.open ? Math.max(MIN_RIGHT_SIDEBAR_WIDTH, rightSidebar.width) : null : readRightSidebarSkeletonWidth();
      $[3] = rightSidebar;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    rightPanelWidth = t3;
  }
  let t3;
  if ($[5] !== isHydrated) {
    t3 = isHydrated && !readLeftSidebarSkeletonLayout().visible;
    $[5] = isHydrated;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const leftSidebarHidden = t3;
  let t4;
  if ($[7] !== isHydrated || $[8] !== sessionId) {
    t4 = isHydrated && sessionId ? readCachedSessionHeaderChrome(sessionId) : null;
    $[7] = isHydrated;
    $[8] = sessionId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const cachedHeader = t4;
  const title = header?.title !== undefined ? header.title : cachedHeader?.title;
  const prLabel = header?.prLabel !== undefined ? header.prLabel : cachedHeader?.prLabel;
  const showPrBadge = rightPanelWidth === null && prLabel !== null;
  let t5;
  if ($[10] !== leftSidebarHidden) {
    t5 = leftSidebarHidden && <div className="flex size-7 shrink-0 items-center justify-center rounded-lg"><PanelLeft className="size-4 text-neutral-600 dark:text-neutral-400" aria-hidden={true} /></div>;
    $[10] = leftSidebarHidden;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== title) {
    t6 = title ? <h2 className="min-w-0 flex-1 truncate type-page-title-l text-neutral-900 dark:text-neutral-100">{title}</h2> : <div className="min-w-0 flex-1"><Skeleton className="h-[25px] w-56 max-w-full rounded" /></div>;
    $[12] = title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== prLabel || $[15] !== showPrBadge) {
    t7 = showPrBadge && (prLabel ? <div data-slot="session-header-pr-skeleton" className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-white px-2 type-chat-prose text-neutral-600 dark:bg-[#18191a] dark:text-neutral-300"><GitPullRequest className="size-3 text-ds-green" aria-hidden={true} /><span>{prLabel}</span></div> : <Skeleton data-slot="session-header-pr-skeleton" className="h-7 w-28 shrink-0 rounded-md" />);
    $[14] = prLabel;
    $[15] = showPrBadge;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex size-7 shrink-0 items-center justify-center rounded-lg"><PanelRight className="size-4 text-neutral-600 dark:text-neutral-400" aria-hidden={true} /></div>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t9 = <div className="flex min-w-0 w-full items-center gap-3">{t5}{t6}{t7}{t8}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== leftSidebarHidden) {
    t10 = leftSidebarHidden ? <div className="w-7 shrink-0" aria-hidden={true} /> : null;
    $[22] = leftSidebarHidden;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="h-4" />;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10) {
    t12 = <div className="flex min-w-0 items-center gap-3">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t9) {
    t13 = <div data-slot="session-header-skeleton" className="flex min-h-16 flex-shrink-0 flex-col justify-center gap-1 bg-[var(--color-app-background)] px-3 py-2 md:px-6">{t9}{t12}</div>;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex flex-col items-end gap-1"><div className="max-w-[85%] overflow-hidden rounded-lg bg-neutral-50 px-4 py-2 md:max-w-[500px] dark:bg-[#242526]"><Skeleton className="h-4 w-[280px] max-w-full rounded" /><Skeleton className="mt-2 h-4 w-[180px] max-w-full rounded" /></div></div>;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="space-y-3"><Skeleton className="h-4 w-full rounded" /><Skeleton className="h-4 w-4/5 rounded" /><Skeleton className="h-4 w-3/5 rounded" /></div>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex flex-col items-end gap-1"><div className="max-w-[85%] overflow-hidden rounded-lg bg-neutral-50 px-4 py-2 md:max-w-[500px] dark:bg-[#242526]"><Skeleton className="h-4 w-[220px] max-w-full rounded" /></div></div>;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mx-auto w-full max-w-[704px] flex-1 space-y-6 px-6 pb-6 pt-3">{t14}{t15}{t16}<div className="space-y-3"><Skeleton className="h-4 w-full rounded" /><Skeleton className="h-4 w-3/4 rounded" /></div></div>;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== rightPanelWidth) {
    t18 = rightPanelWidth === null && <ShortcutsPill className="pointer-events-auto absolute right-4 z-20 -mt-[52px]" />;
    $[34] = rightPanelWidth;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = Boolean(sessionId);
  const t20 = !sessionId;
  let t21;
  if ($[36] !== isHydrated || $[37] !== sessionId) {
    t21 = isHydrated && sessionId ? getDraft(sessionId) : "";
    $[36] = isHydrated;
    $[37] = sessionId;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== sessionId) {
    t22 = event => {
      if (sessionId) {
        saveDraftDebounced(sessionId, event.target.value);
      }
    };
    $[39] = sessionId;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const t23 = `type-chat-prose w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70 ${sessionId ? "pointer-events-auto" : ""}`;
  let t24;
  if ($[41] !== sessionId || $[42] !== t19 || $[43] !== t20 || $[44] !== t21 || $[45] !== t22 || $[46] !== t23) {
    t24 = <textarea key={sessionId} ref={inputRef} rows={1} autoFocus={t19} readOnly={t20} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Add a follow-up" defaultValue={t21} onChange={t22} className={t23} />;
    $[41] = sessionId;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="absolute inset-y-0 right-0 flex items-center"><span className="flex h-7 w-7 items-center justify-center rounded-full border border-black/[0.08] bg-gradient-to-r from-[var(--brand-gradient-blue-tertiary)] to-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500"><ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} /></span></div>;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t24) {
    t27 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t24}{t25}{t26}</div></div>;
    $[50] = t24;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t18 || $[53] !== t27) {
    t28 = <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t17}<div className="mx-auto w-full max-w-[720px] md:max-w-[736px] flex-shrink-0 px-2 pb-2 md:px-4 md:pb-6">{t18}{t27}</div></div>;
    $[52] = t18;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== rightPanelWidth) {
    t29 = rightPanelWidth !== null && <RightSidebarSkeleton width={rightPanelWidth} />;
    $[55] = rightPanelWidth;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28 || $[58] !== t29) {
    t30 = <div className="flex min-h-0 flex-1">{t28}{t29}</div>;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t13 || $[61] !== t30) {
    t31 = <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t13}{t30}</div>;
    $[60] = t13;
    $[61] = t30;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  return t31;
}
function orgSlugFromPath(path: string): string | null {
  return path.split(/[?#]/)[0].split("/").filter(Boolean)[0] ?? null;
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into the autofix card + TaskStarterCards instead, so only
 * `pr_preview` reserves the PrActionCards skeleton shape (four action pills,
 * no suggestion card).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading.
 */
export function NewSessionChatSkeleton() {
  const $ = _c(33);
  const isHydrated = useIsHydrated();
  let isPrAttached;
  let t0;
  if ($[0] !== isHydrated) {
    const orgSlug = isHydrated ? orgSlugFromPath(window.location.pathname) : null;
    isPrAttached = isHydrated && hasPrPreviewInSearch(window.location.search);
    t0 = isHydrated && !isPrAttached && readCachedShowAutofixCard(orgSlug);
    $[0] = isHydrated;
    $[1] = isPrAttached;
    $[2] = t0;
  } else {
    isPrAttached = $[1];
    t0 = $[2];
  }
  const showAutofixCardSkeleton = t0;
  let t1;
  if ($[3] !== isHydrated) {
    t1 = isHydrated && !readLeftSidebarSkeletonLayout().visible && <div className="flex size-7 shrink-0 items-center justify-center rounded-lg"><PanelLeft className="size-4 text-neutral-600 dark:text-neutral-400" aria-hidden={true} /></div>;
    $[3] = isHydrated;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex h-16 flex-shrink-0 items-center bg-[var(--color-app-background)] px-3 md:px-6">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="relative mt-1"><img src={assetUrl("anvil-hello-light.png")} alt="[code]smith" className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden`} /><img src={assetUrl("anvil-hello-dark.png")} alt="[code]smith" className={`hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block`} /></div>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col items-center">{t3}<div className="flex h-[25px] items-center"><Skeleton className="h-5 w-72 max-w-full rounded" /></div></div>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isHydrated) {
    t5 = isHydrated ? peekSkeletonComposerText() : "";
    $[9] = isHydrated;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <textarea rows={1} autoFocus={true} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Got a task or question?" defaultValue={t5} onChange={_temp} className="type-chat-prose pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70" />;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-y-0 right-0 flex items-center"><span className="flex h-7 w-7 items-center justify-center rounded-full border border-black/[0.08] bg-gradient-to-r from-[var(--brand-gradient-blue-tertiary)] to-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500"><ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} /></span></div>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6) {
    t9 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t6}{t7}{t8}</div></div>;
    $[15] = t6;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== showAutofixCardSkeleton) {
    t10 = showAutofixCardSkeleton && <SuggestionCardSkeleton />;
    $[17] = showAutofixCardSkeleton;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS;
  let t12;
  if ($[19] !== isPrAttached) {
    t12 = isPrAttached ? Array.from({
      length: PR_ACTION_PILL_COUNT
    }, _temp2) : <><StarterPromptPillSkeleton className={RIGHTSIZE_STARTER_PILL_LAYOUT_CLASS} /><StarterPromptPillSkeleton className="grow" /></>;
    $[19] = isPrAttached;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t10 || $[25] !== t13) {
    t14 = <div className="flex w-full flex-col gap-4">{t10}{t13}</div>;
    $[24] = t10;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== t9) {
    t15 = <div className={NEW_SESSION_SCROLL_AREA_CLASS}><div className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}>{t4}<div className="flex w-full flex-col gap-3">{t9}{t14}</div></div></div>;
    $[27] = t14;
    $[28] = t9;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t2) {
    t16 = <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t2}{t15}</div>;
    $[30] = t15;
    $[31] = t2;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 */
function _temp2(_, i) {
  return <PromptPillSkeleton key={i} className="w-32" />;
}
function _temp(e) {
  return saveSkeletonComposerText(e.target.value);
}
function isNewSessionPath(path: string): boolean {
  const segments = path.split(/[?#]/)[0].split("/").filter(Boolean);
  return segments[1] === "sessions" && (segments.length === 2 || segments[2] === "new");
}
function sessionIdFromPath(path: string): string | undefined {
  const segments = path.split(/[?#]/)[0].split("/").filter(Boolean);
  return segments[1] === "sessions" && segments[2] !== "new" && !segments[2]?.startsWith("[") ? segments[2] : undefined;
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton() {
  const $ = _c(3);
  const router = useRouter();
  const isHydrated = useIsHydrated();
  const path = isHydrated ? window.location.pathname : router.asPath;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SidebarSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== path) {
    t1 = <div className="pointer-events-none flex h-full">{t0}{isNewSessionPath(path) ? <NewSessionChatSkeleton /> : <SessionChatSkeleton sessionId={sessionIdFromPath(path)} />}</div>;
    $[1] = path;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}